import React from 'react';
import { motion } from 'framer-motion';
import { FaBox, FaPencilAlt, FaTrash, FaDollarSign, FaExternalLinkAlt, FaCopy } from 'react-icons/fa';
import { CURRENCY_SYMBOLS } from '../../utils/currencySymbols';

const ItemCard = ({ item, viewMode, onClick, onEdit, onDelete, onSold, onDuplicate, index, salesCount = 0 }) => {
  const getStockStatus = (qty) => {
    if (qty === 0) return { bg: 'bg-red-100 dark:bg-red-900', text: 'text-red-800 dark:text-red-200', label: 'Out of stock', needsRestock: true };
    if (qty < 3) return { bg: 'bg-yellow-100 dark:bg-yellow-900', text: 'text-yellow-800 dark:text-yellow-200', label: `${qty} in stock`, needsRestock: true };
    return { bg: 'bg-green-100 dark:bg-green-900', text: 'text-green-800 dark:text-green-200', label: `${qty} in stock`, needsRestock: false };
  };

  const stockStatus = getStockStatus(item.quantity || 0);
  const currencySymbol = CURRENCY_SYMBOLS[item.priceCurrency || 'USD'];

  if (viewMode === 'list') {
    return (
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: index * 0.05 }}
        onClick={onClick}
        className="bg-white dark:bg-gray-700 rounded-lg p-3 sm:p-4 flex flex-col sm:flex-row 
          sm:justify-between sm:items-center gap-3 hover:shadow-lg transition-all cursor-pointer select-none"
      >
        <div className="flex items-center space-x-3 sm:space-x-4 flex-1 min-w-0">
          {/* Product Image or Icon */}
          {item.imageUrl ? (
            <img 
              src={item.imageUrl} 
              alt={item.name}
              className="h-12 w-12 sm:h-16 sm:w-16 rounded-lg object-cover flex-shrink-0"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div 
            className="h-12 w-12 sm:h-16 sm:w-16 bg-gradient-to-r from-teal-400 to-cyan-600 rounded-lg 
              flex items-center justify-center flex-shrink-0"
            style={{ display: item.imageUrl ? 'none' : 'flex' }}
          >
            <FaBox className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white truncate">
              {item.name}
            </h3>
            <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-1">
              <span className="text-base sm:text-lg font-bold text-teal-600 dark:text-teal-400">
                {currencySymbol}{parseFloat(item.price || 0).toFixed(2)}
              </span>
              <span className={`text-xs px-2 py-1 rounded-full ${stockStatus.bg} ${stockStatus.text}`}>
                {stockStatus.label}
              </span>
              {salesCount > 0 && (
                <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">
                  📊 {salesCount} sold
                </span>
              )}
              {item.sku && (
                <span className="text-xs text-gray-500 dark:text-gray-400 hidden md:block">
                  SKU: {item.sku}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end sm:justify-start gap-1 sm:gap-2 flex-shrink-0">
          {item.productUrl && (
            <a
              href={item.productUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="p-2 sm:p-2.5 hover:bg-teal-100 dark:hover:bg-teal-900 rounded-lg transition-colors"
              title="View Product Page"
            >
              <FaExternalLinkAlt className="h-4 w-4 sm:h-5 sm:w-5 text-teal-600 dark:text-teal-400" />
            </a>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onSold(); }}
            className="p-2 sm:p-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Record Sale"
          >
            <FaDollarSign className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 hover:text-green-600 dark:hover:text-green-400" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
            className="p-2 sm:p-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Duplicate Item"
          >
            <FaCopy className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            className="p-2 sm:p-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Edit Item"
          >
            <FaPencilAlt className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="p-2 sm:p-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Delete Item"
          >
            <FaTrash className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 hover:text-red-600" />
          </button>
        </div>
      </motion.div>
    );
  }

  // Grid view
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      onClick={onClick}
      className="bg-white dark:bg-gray-700 rounded-xl overflow-hidden shadow-lg 
        hover:shadow-2xl transition-all cursor-pointer select-none"
    >
      {/* Product Image or Placeholder */}
      {item.imageUrl ? (
        <div className="h-48 bg-gray-100 dark:bg-gray-900 flex items-center justify-center relative">
          <img 
            src={item.imageUrl} 
            alt={item.name}
            className="h-full w-full object-contain"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div 
            className="absolute inset-0 bg-gradient-to-r from-teal-400 to-cyan-600 flex items-center justify-center"
            style={{ display: 'none' }}
          >
            <FaBox className="h-16 w-16 text-white" />
          </div>
        </div>
      ) : (
        <div className="h-48 bg-gradient-to-r from-teal-400 to-cyan-600 flex items-center justify-center">
          <FaBox className="h-16 w-16 text-white" />
        </div>
      )}
      
      <div className="p-4">
        <div className="flex justify-between items-start mb-2">
          <h3 className="font-semibold text-gray-900 dark:text-white line-clamp-2 flex-1">
            {item.name}
          </h3>
        </div>

        <div className="mb-3">
          <span className={`inline-block text-xs px-2 py-1 rounded-full ${stockStatus.bg} ${stockStatus.text}`}>
            {stockStatus.label}
          </span>
        </div>
        
        <div className="flex justify-between items-center">
          <span className="text-lg font-bold text-teal-600 dark:text-teal-400">
            {currencySymbol}{parseFloat(item.price || 0).toFixed(2)}
          </span>
          
          <div className="flex space-x-1.5">
            {item.productUrl && (
              <a
                href={item.productUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="p-2 hover:bg-teal-100 dark:hover:bg-teal-900 rounded-lg transition-colors"
                title="View Product Page"
              >
                <FaExternalLinkAlt className="h-4 w-4 text-teal-600 dark:text-teal-400" />
              </a>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); onSold(); }}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Record Sale"
            >
              <FaDollarSign className="h-4 w-4 text-gray-400 hover:text-green-600 dark:hover:text-green-400" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Duplicate Item"
            >
              <FaCopy className="h-4 w-4 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Edit Item"
            >
              <FaPencilAlt className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-yellow-400" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Delete Item"
            >
              <FaTrash className="h-4 w-4 text-gray-400 hover:text-red-600" />
            </button>
          </div>
        </div>

        {(item.sku || salesCount > 0) && (
          <div className="mt-2 pt-2 border-t dark:border-gray-700 flex justify-between items-center">
            {item.sku && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                SKU: {item.sku}
              </span>
            )}
            {salesCount > 0 && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                📊 {salesCount} sold
              </span>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ItemCard;

